import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const Icon = {
  document: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display:'inline-block', verticalAlign:'middle', marginRight:'5px' }}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display:'inline-block', verticalAlign:'middle', marginRight:'4px' }}>
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  copy: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
    </svg>
  ),
  send: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  ),
};

const QUICK_PROMPTS = [
  'Write a follow-up email after applying',
  'Write a thank-you email after an interview',
  'Write a cold outreach email to a recruiter',
  'Write a salary negotiation email',
  'Write a referral request email',
  'Make the last email shorter and more direct',
];

function typeLabel(mime) {
  if (mime === 'application/pdf') return 'PDF';
  if (mime?.includes('wordprocessingml') || mime?.includes('msword')) return 'Word';
  if (mime?.startsWith('image/')) return 'Image';
  return 'File';
}

const GREETING = {
  role: 'assistant',
  content: `Hi! I'm your AI email assistant — I can write personalized recruiter and hiring manager emails for you.\n\nTo get the best results:\n• Select a job application below for company/role context\n• Select a contact if you have the recruiter's info\n• Choose your resume/CV from Documents so I can reference your actual achievements\n\nWhat would you like to write?`,
};

export default function AIEmailPage({ applications, contacts, API_URL }) {
  const [messages, setMessages] = useState([GREETING]);
  const [historyLoading,    setHistoryLoading]    = useState(true);
  const [input,             setInput]             = useState('');
  const [loading,           setLoading]           = useState(false);
  const [selectedAppId,     setSelectedAppId]     = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [copiedIdx,         setCopiedIdx]         = useState(null);

  // Documents
  const [docs,             setDocs]             = useState([]);
  const [docsLoading,      setDocsLoading]      = useState(true);
  const [selectedDocIds,   setSelectedDocIds]   = useState([]);
  const [docTexts,         setDocTexts]         = useState({});
  const [fetchingDocIds,   setFetchingDocIds]   = useState(new Set());
  const [showDocPicker,    setShowDocPicker]    = useState(false);

  const endRef   = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  // Load persisted chat history
  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API_URL}/api/ai/history`);
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setMessages([GREETING, ...data.map(r => ({ role: r.role, content: r.content }))]);
        }
      } catch { /* silent */ } finally { setHistoryLoading(false); }
    })();
  }, [API_URL]);

  // Load document list
  const fetchDocs = useCallback(async () => {
    try {
      const res  = await fetch(`${API_URL}/api/documents`);
      const data = await res.json();
      setDocs(Array.isArray(data) ? data.filter(d => d.mime_type !== 'image/png' && d.mime_type !== 'image/jpeg' && d.mime_type !== 'image/gif' && d.mime_type !== 'image/webp') : []);
    } catch { /* silent */ } finally { setDocsLoading(false); }
  }, [API_URL]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Fetch text for a document once (cached in docTexts)
  const ensureDocText = useCallback(async (id) => {
    if (docTexts[id] !== undefined || fetchingDocIds.has(id)) return;
    setFetchingDocIds(prev => new Set(prev).add(id));
    try {
      const res  = await fetch(`${API_URL}/api/documents/${id}/text`);
      const data = await res.json();
      setDocTexts(prev => ({ ...prev, [id]: data.text_content || '' }));
    } catch {
      setDocTexts(prev => ({ ...prev, [id]: '' }));
    }
    setFetchingDocIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  }, [API_URL, docTexts, fetchingDocIds]);

  // Toggle document selection and pre-fetch its text
  const toggleDoc = (id) => {
    setSelectedDocIds(prev => {
      if (prev.includes(id)) return prev.filter(d => d !== id);
      ensureDocText(id);
      return [...prev, id];
    });
  };

  // Combined resume text from all selected docs
  const combinedResumeText = selectedDocIds
    .map(id => {
      const doc  = docs.find(d => d.id === id);
      const text = docTexts[id];
      if (!text) return '';
      return `[${doc?.name || doc?.original_name || 'Document'}]\n${text}`;
    })
    .filter(Boolean)
    .join('\n\n---\n\n');

  const getContext = () => {
    const application = selectedAppId     ? applications.find(a => a.id === parseInt(selectedAppId))     || null : null;
    const contact     = selectedContactId ? contacts.find(c => c.id === parseInt(selectedContactId))     || null : null;
    return { application, contact, resumeText: combinedResumeText };
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: msg };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setLoading(true);

    // If any selected doc text hasn't loaded yet, wait briefly
    const pendingIds = selectedDocIds.filter(id => docTexts[id] === undefined);
    if (pendingIds.length) {
      await Promise.all(pendingIds.map(id => ensureDocText(id)));
    }

    try {
      const history = updated.slice(1, -1);
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, context: getContext(), history }),
      });
      const data = await res.json();
      if (data.error) {
        const raw = data.error;
        let friendly = raw;
        if (raw.includes('overloaded') || raw.includes('high demand') || raw.includes('503') || raw.includes('UNAVAILABLE')) {
          friendly = '⏳ The AI is temporarily busy. Please wait a moment and try again.';
        } else if (raw.includes('quota') || raw.includes('RESOURCE_EXHAUSTED') || raw.includes('429')) {
          friendly = '🚫 Gemini API quota exceeded. Check your quota at console.cloud.google.com.';
        } else if (raw.includes('API key') || raw.includes('PERMISSION')) {
          friendly = '🔑 API key error — check your GEMINI_API_KEY in the backend .env file.';
        }
        setMessages(prev => [...prev, { role: 'assistant', content: friendly, isError: true }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply || 'No response received.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Could not reach the backend. Is it running at ' + API_URL + '?' }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const copyMsg = async (content, idx) => {
    await navigator.clipboard.writeText(content);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const appContacts  = selectedAppId ? contacts.filter(c => String(c.application_id) === selectedAppId) : contacts;
  const selectedApp  = applications.find(a => a.id === parseInt(selectedAppId));
  const selectedDocs = docs.filter(d => selectedDocIds.includes(d.id));

  const [dismissedFollowups, setDismissedFollowups] = useState([]);
  const staleApps = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 14);
    return applications
      .filter(a => a.status === 'Applied' && new Date(a.applied_date || a.created_at) < cutoff)
      .slice(0, 4);
  }, [applications]);

  return (
    <div className="ai-page-wrapper">
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">AI Email Assistant</h2>
          <p className="page-sub">
            {selectedDocs.length > 0 && <>{Icon.check}{selectedDocs.length} doc{selectedDocs.length > 1 ? 's' : ''} loaded · </>}
            {selectedApp ? `${selectedApp.company} — ${selectedApp.role}` : 'No job selected'}
          </p>
        </div>
        <button
          className={selectedDocIds.length > 0 ? 'btn-secondary' : 'btn-primary'}
          style={{ fontSize: '0.82rem', position: 'relative' }}
          onClick={() => setShowDocPicker(v => !v)}
        >
          {Icon.document}
          {selectedDocIds.length > 0
            ? <>{Icon.check}{selectedDocIds.length} Document{selectedDocIds.length > 1 ? 's' : ''}</>
            : 'Choose Documents'}
        </button>
      </div>

      {/* Follow-up reminders */}
      {staleApps.filter(a => !dismissedFollowups.includes(a.id)).length > 0 && (
        <div className="followup-banner">
          <span className="followup-banner-title">📬 Follow-up reminders</span>
          {staleApps.filter(a => !dismissedFollowups.includes(a.id)).map(app => {
            const days = Math.floor((new Date() - new Date(app.applied_date || app.created_at)) / 86400000);
            return (
              <div key={app.id} className="followup-item">
                <span className="followup-info">
                  <strong>{app.role}</strong> at {app.company}
                  <span className="followup-days">{days}d ago</span>
                </span>
                <button
                  className="followup-write-btn"
                  onClick={() => { setSelectedAppId(String(app.id)); setDismissedFollowups(p => [...p, app.id]); }}
                >
                  Write follow-up ↗
                </button>
                <button className="followup-dismiss" onClick={() => setDismissedFollowups(p => [...p, app.id])}>×</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Document picker panel */}
      {showDocPicker && (
        <div className="resume-panel">
          <div className="resume-panel-title">
            Choose Documents for AI Context
            <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#64748b', marginLeft: '8px' }}>
              — text is extracted automatically on upload
            </span>
          </div>

          {docsLoading ? (
            <p style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Loading documents…</p>
          ) : docs.length === 0 ? (
            <p style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
              No documents with extractable text yet. Upload a PDF or Word file in the{' '}
              <strong>Documents</strong> section first.
            </p>
          ) : (
            <div className="doc-picker-list">
              {docs.map(doc => {
                const selected = selectedDocIds.includes(doc.id);
                const loading  = fetchingDocIds.has(doc.id);
                return (
                  <label key={doc.id} className={`doc-picker-item ${selected ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleDoc(doc.id)}
                      style={{ display: 'none' }}
                    />
                    <div className="doc-picker-check">{selected && '✓'}</div>
                    <div className="doc-picker-info">
                      <div className="doc-picker-name">{doc.name || doc.original_name}</div>
                      <div className="doc-picker-meta">
                        <span className={`doc-type-badge doc-type-${typeLabel(doc.mime_type).toLowerCase()}`}>
                          {typeLabel(doc.mime_type)}
                        </span>
                        {loading && <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>loading text…</span>}
                        {!loading && selected && docTexts[doc.id] && (
                          <span style={{ color: '#10b981', fontSize: '0.72rem' }}>
                            {Icon.check}{docTexts[doc.id].length.toLocaleString()} chars
                          </span>
                        )}
                        {!loading && selected && docTexts[doc.id] === '' && (
                          <span style={{ color: '#f59e0b', fontSize: '0.72rem' }}>⚠ No text extracted — re-upload to fix</span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {selectedDocIds.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <button className="btn-primary" style={{ fontSize: '0.78rem', padding: '5px 12px' }} onClick={() => setShowDocPicker(false)}>
                {Icon.check}Done
              </button>
              <button className="btn-secondary" style={{ fontSize: '0.78rem', padding: '5px 12px' }} onClick={() => setSelectedDocIds([])}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {/* Context bar */}
      <div className="ai-context-bar">
        <div className="context-selector">
          <label>Job Application</label>
          <select value={selectedAppId} onChange={e => { setSelectedAppId(e.target.value); setSelectedContactId(''); }}>
            <option value="">— Select a job for context —</option>
            {applications.map(a => (
              <option key={a.id} value={a.id}>{a.company} — {a.role} ({a.status})</option>
            ))}
          </select>
        </div>
        <div className="context-selector">
          <label>Contact / Recruiter</label>
          <select value={selectedContactId} onChange={e => setSelectedContactId(e.target.value)}>
            <option value="">— Select a contact (optional) —</option>
            {appContacts.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.title ? ` · ${c.title}` : ''}</option>
            ))}
          </select>
        </div>
        <button
          className="btn-secondary"
          style={{ fontSize: '0.78rem', padding: '6px 12px', alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
          onClick={async () => {
            await fetch(`${API_URL}/api/ai/history`, { method: 'DELETE' }).catch(() => {});
            setMessages([GREETING]);
            setSelectedAppId('');
            setSelectedContactId('');
            setSelectedDocIds([]);
            setShowDocPicker(false);
          }}
        >
          Clear Chat
        </button>
      </div>

      {/* Chat messages */}
      <div className="ai-chat-area">
        {historyLoading && (
          <div style={{ textAlign: 'center', padding: '12px', color: '#94a3b8', fontSize: '0.8rem' }}>
            Loading chat history…
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`chat-bubble ${msg.role}`}>
            <div className="chat-bubble-content">
              <pre className="chat-text">{msg.content}</pre>
              {msg.role === 'assistant' && i > 0 && (
                <button className="chat-copy-btn" onClick={() => copyMsg(msg.content, i)} title="Copy to clipboard">
                  {copiedIdx === i ? Icon.check : Icon.copy}
                </button>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="chat-bubble assistant">
            <div className="chat-bubble-content">
              <div className="chat-typing"><span /><span /><span /></div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input area */}
      <div className="ai-input-area">
        <div className="ai-quick-prompts">
          {QUICK_PROMPTS.map(p => (
            <button key={p} className="quick-prompt-btn" onClick={() => send(p)}>{p}</button>
          ))}
        </div>
        <div className="ai-input-row">
          <textarea
            ref={inputRef}
            className="ai-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask me to write or improve an email… (Enter to send, Shift+Enter for new line)"
            rows={2}
          />
          <button className="ai-send-btn" onClick={() => send()} disabled={loading || !input.trim()}>
            {loading ? <span className="chat-typing" style={{ display:'inline-flex', gap:'3px' }}><span/><span/><span/></span> : Icon.send}
          </button>
        </div>
      </div>
    </div>
  );
}
